import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { takeUntil, filter, map } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { BaseGenderOptions } from '../../../../../shared/constants/base-gender-options';
import { BaseButtonSize } from '../../../../../shared/components/button/base-button.component';
import { BranchListItemResponse, GroupListItemResponse } from '../../../../../../api';
import { InviteUserPspForm, InviteUserCustomerForm } from '../../../_interfaces/base-user-management.interface';
import { BASE_ROLES } from '../../../../../core/constants/base-roles';
import { BaseRolesService } from '../../../../../core/services/base-roles.service';
import {
  BaseCoreState,
  baseSharedSelectors,
  baseSharedActions,
  baseAdministrationActions,
} from '../../../../../_store';
import { BaseUserManagementService } from '../../../_services/base-user-management.service';
import { BaseNameIdInterface, DepartmentShortInfo } from '../../../../../shared/interfaces/base-name-id.interface';

type InviteUserFormGroup = FormGroup<InviteUserCustomerForm> | FormGroup<InviteUserPspForm>;

@Component({
  selector: 'base-invite-user-form',
  templateUrl: './base-invite-user-form.component.html',
})
export class BaseInviteUserFormComponent implements OnInit, OnDestroy {
  @Input() form: InviteUserFormGroup;
  @Input() modalRef: NgbModalRef;
  @Input() groups: GroupListItemResponse[];
  @Input() pspBranches: BranchListItemResponse[];
  @Input() isFormSubmitted = false;
  @Input() isPSP = false;
  @Output() inviteUser = new EventEmitter<boolean>();

  genderOptions = BaseGenderOptions;
  buttonSizes = BaseButtonSize;
  baseRoles = BASE_ROLES;
  isAdmin = false;
  isWorkCouncil = false;
  customerDepartments: DepartmentShortInfo[] = [];
  locations: BaseNameIdInterface[] = [];

  private destroy$ = new Subject();

  constructor(
    private rolesService: BaseRolesService,
    private store: Store<BaseCoreState>,
    private userManagementService: BaseUserManagementService
  ) {}

  get hasGroupControl(): boolean {
    return this.form.contains('group');
  }

  get hasCustomerLocationControl(): boolean {
    return this.form.contains('customerLocations');
  }

  get hasCustomerDepartmentsControl(): boolean {
    return this.form.contains('customerDepartments');
  }

  hasCustomerLocationsControlGuard(form: InviteUserFormGroup): form is FormGroup<InviteUserCustomerForm> {
    return 'customerLocations' in form.controls;
  }

  hasRoleControlGuard(form: InviteUserFormGroup): form is FormGroup<InviteUserCustomerForm> {
    return 'role' in form.controls;
  }

  ngOnInit(): void {
    this.isAdmin = this.rolesService.hasAdminRole();
    this.isWorkCouncil = this.rolesService.hasWorkCouncilRole();

    if (!this.isPSP) {
      this.getCustomerLocations();
      this.getCustomerDepartments();
      this.customerFormRoleControlHandler(this.form as FormGroup<InviteUserCustomerForm>);
    }
  }

  ngOnDestroy() {
    if (!this.isPSP) {
      this.userManagementService.removeOptionalFormControls(this.form as FormGroup<InviteUserCustomerForm>);
      this.store.dispatch(baseSharedActions.resetCustomerDepartmentList());
      this.store.dispatch(baseAdministrationActions.resetLocations());
    }
  }

  confirmSendingInvitation(): void {
    this.isFormSubmitted = true;
    this.inviteUser.emit();
  }

  cancelSendingInvitation(): void {
    this.modalRef.close();
  }

  customerFormRoleControlHandler(form: FormGroup<InviteUserCustomerForm>) {
    form.controls.role.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((role) => {
      switch (role) {
        case BASE_ROLES.CUSTOMER_WORK_COUNCIL:
          this.userManagementService.addControls(form, ['customerLocations']);
          this.userManagementService.removeControls(form, ['group', 'customerDepartments']);
          break;
        case BASE_ROLES.CUSTOMER_USER:
          this.userManagementService.addControls(form, ['group', 'customerDepartments']);
          this.userManagementService.removeControls(form, ['customerLocations']);
          break;
        case BASE_ROLES.CUSTOMER_ADMIN:
          this.userManagementService.removeOptionalFormControls(form);
      }
    });
  }

  private getCustomerLocations() {
    this.userManagementService
      .getCustomerLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe((locations) => (this.locations = locations));
  }

  private getCustomerDepartments() {
    this.store
      .select(baseSharedSelectors.getDepartments)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map(
          (value) =>
            (value.data || []).map((dep) => ({
              name: dep.primaryData.name,
              id: dep.id,
              locationName: dep.location.name,
            })) as DepartmentShortInfo[]
        )
      )
      .subscribe((departments) => (this.customerDepartments = departments));

    this.store.dispatch(baseSharedActions.loadCustomerDepartmentList({ payload: {} }));
  }
}
